// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.ReportPartGridPivot.js" company="Izenda">  Copyright
// (c) 2015 Izenda, Inc.  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and  International
// Copyright Laws. Unauthorized reproduction,  reverse-engineering, and
// distribution of all or any portion of  the code contained in this file is
// strictly prohibited and may  result in severe civil and criminal penalties
// and will be  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES  ARE CONFIDENTIAL AND
// PROPRIETARY TRADE  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
// LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET  CONTROLS AS
// PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED  FILES OR ANY
// PORTION OF ITS CONTENTS SHALL AT NO TIME BE  COPIED, TRANSFERRED, SOLD,
// DISTRIBUTED, OR OTHERWISE MADE  AVAILABLE TO OTHER INDIVIDUALS WITHOUT
// EXPRESS WRITTEN CONSENT  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON  ADDITIONAL
// RESTRICTIONS.
//
// </copyright>
// ----------------------------------------------------------------------

/**
 * ReportPartGridPivot component
 */
import React, { Component, PropTypes } from 'react';
import classnames from 'classnames';
import { propTypes as MobxPropTypes } from 'mobx-react';
import connect from 'share/enhancers/Izenda.Connect';

import { Table, Column, ColumnGroup } from 'share/components/controls/DataGrid/Izenda.Table';
import SubReportLinkUtils from './Izenda.SubReportLinkUtils';
import SubTotalCell from './Izenda.SubTotalCell';
import constants from 'logic/data/Izenda.ReportPartGridConstants';
import ProcessData from 'logic/Izenda.ReportPartGridProcessData';
import ColumnDataObject from 'logic/models/Izenda.ColumnDataObject';

import reportPartUtils from 'share/utils/Izenda.ReportPartUtils';
import ReportPartUtils from 'share/utils/Izenda.ReportPartUtils';
import PivotExportingUtils from './Izenda.PivotExportingUtils';
import stylingUtil from './Izenda.ReportPartGridStylingUtil';
import PrintingUtils from 'share/utils/Izenda.PrintingUtils';
import actions from './Izenda.ReportPartGridPivotActions';
import {
  get,
  take,
  concat,
  head,
  last,
  flatMap,
  every,
  clone,
  isEmpty,
  flow,
  map,
  getOr,
  sortBy,
  findIndex,
  negate,
  find
} from 'lodash/fp';

class ReportPartGridPivot extends Component {
  static propTypes = {
    reportPart: PropTypes.object.isRequired,
    records: MobxPropTypes.arrayOrObservableArray.isRequired,
    loader: PropTypes.object.isRequired
  };

  constructor() {
    super();
    this.renderListeners = [];
  }

  componentDidMount() {
    const { printingMode, finishedRender } = this.props;

    printingMode &&
      PrintingUtils.publishFinishedRender(this.renderListeners, () => {
        finishedRender && finishedRender();
      });
  }

  render() {
    const {
      rowDataObject,
      haveSupTotal,
      forDrillDown,
      isPreview,
      allGroupSuptotal,
      allRow,
      supTotalDataRow,
      getFirstRecordForDrilldown,
      pageStatus
    } = this.props;

    let newData = ProcessData.flattenRowDataObject(rowDataObject, forDrillDown, haveSupTotal);
    isPreview && (newData = take(constants.MAX_ROW, newData));

    // for Pivot only
    allGroupSuptotal && (newData = concat(newData, supTotalDataRow));

    // add 'All' row at the first row for drillDown only
    allRow && (newData = getFirstRecordForDrilldown(newData));

    const numberOfRow = newData.length - 1;

    // if sucess render table using strategy 1 include these case :
    // view mode
    // drilldown export mode
    // pivot export mode : all first level have the same pivot column
    if (pageStatus) {
      return this.renderTable(newData, numberOfRow);
    }
    // render pivot table when column not match with number of pivot column per pages;
    return this.renderPageBreakPerPivotColumnTable(newData, numberOfRow);
  }

  // deep first, to get all side total columns.
  renderPageBreakPerPivotColumnSideTotaltable = (columnDataObjects: any, numberOfRow: any, columnLocInfo: any) => {
    const firstItem = head(columnDataObjects);
    const lastItem = last(columnDataObjects);

    return flatMap(header => {
      if (
        header.type === ColumnDataObject.COLUMN_TYPE.COLUMN &&
        every({ type: ColumnDataObject.COLUMN_TYPE.VALUE }, header.childs)
      ) {
        return [];
      }

      const currentColumnLocInfo = clone(columnLocInfo);

      if (currentColumnLocInfo.firstColumn && header !== firstItem) {
        currentColumnLocInfo.firstColumn = false;
      }

      if (currentColumnLocInfo.lastColumn && header !== lastItem) {
        currentColumnLocInfo.lastColumn = false;
      }

      const childSideTotalColumnLocInfo = clone(currentColumnLocInfo);

      childSideTotalColumnLocInfo.lastColumn = false;

      const childSideTotal = this.renderPageBreakPerPivotColumnSideTotaltable(
        header.childs,
        numberOfRow,
        childSideTotalColumnLocInfo
      );

      currentColumnLocInfo.firstColumn && !isEmpty(childSideTotal) && (currentColumnLocInfo.firstColumn = false);
      // all child's side total will appear before self side total;

      return concat(childSideTotal, this.renderExpandSideTotal(header, numberOfRow, currentColumnLocInfo));
    }, columnDataObjects);
  };

  // each pages have many ColumnDataObject
  renderChildPage = (childPages: any, numberOfRow: any, columnLocInfoOriginal: any) => {
    const lastColumnIndex = childPages.length - 1;

    return childPages.map((childPage, index) => {
      const currentColumnLocInfo = clone(columnLocInfoOriginal);

      currentColumnLocInfo.firstColumn && index !== 0 && (currentColumnLocInfo.firstColumn = false);
      currentColumnLocInfo.lastColumn && index !== lastColumnIndex && (currentColumnLocInfo.lastColumn = false);
      return this.renderGroupingHeadersExporting(childPage, numberOfRow, currentColumnLocInfo);
    });
  };

  renderPageBreakPerPivotColumnTable = (dataRow: any, numberOfRow: any) => {
    const {
      columnDataObjects,
      sideTotalColumns,
      pivotColumnsPerExportedPage,
      fullRowHeader,
      onlyColumnGroupRowHeader,
      haveGroupForRow,
      afterRenderCallBack,
      staticRender,
      reportPart,
      getStickyValue
    } = this.props;

    const properties = reportPart.getReportPartProperties();
    const lastPageRowColumnLogInfo = { firstColumn: true, lastColumn: false, deep: 0 };
    const rowColumnHeader = this.renderGroupingHeaders(fullRowHeader, numberOfRow, lastPageRowColumnLogInfo);

    const pages = PivotExportingUtils.breakPage(columnDataObjects, pivotColumnsPerExportedPage);
    const lastPageColumnLogInfo = { firstColumn: true, lastColumn: true, deep: 0 };

    let pagesVirtualDom = flow(
      map(childPages => this.renderChildPage(childPages, numberOfRow, lastPageColumnLogInfo)),
      map(concat(rowColumnHeader))
    )(pages);

    // last page is for side total column.
    if (!isEmpty(sideTotalColumns)) {
      const lastPageRow = this.renderGroupingHeaders(onlyColumnGroupRowHeader, numberOfRow, lastPageRowColumnLogInfo);
      const lastPageColumn = this.renderPageBreakPerPivotColumnSideTotaltable(
        columnDataObjects,
        numberOfRow,
        lastPageRowColumnLogInfo
      );

      const allSideTotalColumnLogInfo = { firstColumn: false, lastColumn: true, deep: 0 };
      const allSideTotal = this.renderGroupingHeaders(sideTotalColumns, numberOfRow, allSideTotalColumnLogInfo);

      let sideTotalColumn = concat(lastPageColumn, allSideTotal);

      if (haveGroupForRow) {
        sideTotalColumn = [
          <ColumnGroup isHidden key="sideTotal">
            {sideTotalColumn}
          </ColumnGroup>
        ];
      }

      const lastPage = concat(lastPageRow, sideTotalColumn);

      pagesVirtualDom = concat(pagesVirtualDom, [lastPage]);
    }

    return (
      <section>
        {pagesVirtualDom.map((pageVirtualDom, index) => (
          <section key={index} className="report-part-grid-preview">
            {index > 0 && <div className="u_alwaysBreakAfter" />}
            <Table
              showScroll={false}
              dataSource={dataRow}
              className="custom-table pivot-table"
              autoResize={false}
              afterRenderCallBack={afterRenderCallBack}
              staticRender={staticRender}
              onResizeStopCallback={this.onResizeStopCallback}
              stickVertical={getStickyValue(properties.isHeaderStickVertical)}
              stickHorizontal={getStickyValue(properties.isHeaderStickHorizontal)}
            >
              {pageVirtualDom}
            </Table>
          </section>
        ))}
      </section>
    );
  };

  renderTable = (dataRow: any, numberOfRow: any) => {
    const {
      localization,
      pageColumnHeader,
      forDrillDown,
      reportPart,
      expandAll,
      collapseAll,
      afterRenderCallBack,
      staticRender,
      getStickyValue
    } = this.props;
    const properties = reportPart.getReportPartProperties();

    return (
      <section>
        {forDrillDown &&
          !reportPart.reportPartContent.useSeparator && (
            <div>
              <span
                className="icon-plus-circled"
                title={localization.getText('REPORT_PART_GRID_CONSTANTS_LABEL_EXPAND_ALL_TOOLTIP')}
                onClick={expandAll}
              />
              <span
                className="icon-minus-circled"
                title={localization.getText('REPORT_PART_GRID_CONSTANTS_LABEL_COLLAPSE_ALL_TOOLTIP')}
                onClick={collapseAll}
              />
            </div>
          )}
        {pageColumnHeader.map((page, index) => (
          <section key={index} className="report-part-grid-preview">
            {index > 0 && <div className="u_alwaysBreakAfter" />}
            <Table
              showScroll={false}
              dataSource={dataRow}
              className="custom-table pivot-table"
              autoResize={false}
              afterRenderCallBack={afterRenderCallBack}
              staticRender={staticRender}
              onResizeStopCallback={this.onResizeStopCallback}
              stickVertical={getStickyValue(properties.isHeaderStickVertical)}
              stickHorizontal={getStickyValue(properties.isHeaderStickHorizontal)}
            >
              {this.renderGroupingHeaders(page, numberOfRow)}
            </Table>
          </section>
        ))}
      </section>
    );
  };

  onResizeStopCallback = (resizeInfo: any) => {
    const { index, columnWidth } = resizeInfo;
    const {
      reportPart: { reportPartContent }
    } = this.props;
    const {
      rows: { elements: rowCollection },
      values: { elements: valueCollection },
      gridStyle
    } = reportPartContent;
    const isPivotGrid = gridStyle === constants.GRID_STYLES.PIVOT.value;

    // Pivot cannot change value element width by resizer, only Drilldown
    const field =
      index < rowCollection.length
        ? find({ position: index + 1 }, rowCollection)
        : isPivotGrid
          ? null
          : find({ position: index - rowCollection.length + 1 }, valueCollection);

    field && field.updateColumnWidth(columnWidth);
  };

  renderCollapsedSideTotal = (sideTotals: any, numberOfRow: any, level: any, collapsedCurrrentColumnLocInfo: any) => {
    const blankHeader = ProcessData.createBlankHeaders(1, level - 1, sideTotals, true);

    return this.renderGroupingHeaders([blankHeader], numberOfRow, collapsedCurrrentColumnLocInfo);
  };

  renderCollapsedIcon = header => {
    const props = this.props;
    const columnExpanders = getOr(false, 'reportPart.reportPartContent.columns.properties.useExpanders', props);
    const classNameExpandCollapse = classnames(
      'custom-expand-collapse-icon',
      header.isExpand ? 'icon-minus-circled' : 'icon-plus-circled'
    );
    // condition for appear expand/collapse icon
    const condition =
      !header.isLeaf && header.sideTotals && header.sideTotals.length > 0 && columnExpanders && !props.printingMode;

    return condition ? <span className={classNameExpandCollapse} onClick={header.toggleExpand.bind(header)} /> : null;
  };

  renderExpandSideTotal = (header: any, numberOfRow: any, currentColumnLocInfo: any) => {
    const props = this.props;
    const gridWidth = get('reportPart.reportPartContent.properties.columns.width.value', props);
    const {
      getCellData,
      getCellStyle,
      getCellContentStyle,
      fnGetHeaderCellStyle,
      fnGetHeaderCellContentStyle
    } = this.props;
    const sortSideTotal = sortBy('element.position', header.sideTotals);
    const lastIndexSideTotal = sortSideTotal.length - 1;

    return sortSideTotal.map((sideTotal, sideTotalIndex) => {
      const getSupTotalTitle = () => sideTotal.sideTotalTitle;
      const sideTotalColumnLocInfo = clone(currentColumnLocInfo);

      sideTotalColumnLocInfo.firstColumn && sideTotalIndex !== 0 && (sideTotalColumnLocInfo.firstColumn = false);
      sideTotalColumnLocInfo.lastColumn &&
        sideTotalIndex !== lastIndexSideTotal &&
        (sideTotalColumnLocInfo.lastColumn = false);

      const columnWidthValue = get('element.properties.dataFormattings.width.value', sideTotal);
      const width = columnWidthValue || gridWidth || undefined;

      return (
        <Column
          getTitle={getSupTotalTitle}
          width={width}
          cell={this.renderCellContent.bind(this, sideTotal)}
          fnGetCellData={getCellData(sideTotal)}
          fnGetCellStyle={getCellStyle(sideTotal, sideTotalColumnLocInfo, numberOfRow)}
          fnGetCellContentStyle={getCellContentStyle(sideTotal)}
          fnGetHeaderCellStyle={fnGetHeaderCellStyle(sideTotal, sideTotalColumnLocInfo, numberOfRow)}
          fnGetHeaderCellContentStyle={fnGetHeaderCellContentStyle(sideTotal)}
          key={sideTotal.key}
        />
      );
    });
  };

  getTitle = (header: any, afterRenderCallBack: any) => {
    const { reportPart, renderHeader, printingMode, getFilterInfoForSubReport } = this.props;
    const previewDataFieldsMapping = reportPart.previewDataFieldsMapping;
    let title = header.title;
    const fieldElement = get('element', header);

    if (header.isLeaf || !fieldElement) {
      if (header.type !== ColumnDataObject.COLUMN_TYPE.ROW) {
        return title;
      }
      return renderHeader(reportPart.reportPartContent.rows, fieldElement, title, afterRenderCallBack);
    }
    const fieldElementProperties = get('properties', fieldElement);
    const rawRecord = header.data;

    title = ReportPartUtils.getDataFormat(
      title,
      {
        alternativeText: get('dataFormattings.alternativeText', fieldElementProperties),
        fieldFormatData: get('dataFormattings.format.format', fieldElementProperties),
        fieldDataType: get('previewDataType', fieldElement)
      },
      {
        element: reportPartUtils.buildDataSourceElement(reportPart, fieldElement),
        record: rawRecord[0]
      }
    );

    const numberOfPointActions = get('numberOfPointActions', fieldElement) || 0;

    if (numberOfPointActions === 0) {
      return title;
    }
    return SubReportLinkUtils.applySubReportLink(
      reportPart,
      title,
      header,
      rawRecord,
      previewDataFieldsMapping,
      afterRenderCallBack,
      printingMode,
      this.renderListeners,
      null,
      getFilterInfoForSubReport
    );
  };

  renderCellContent = (
    columnData,
    rowData,
    columnKey,
    rowIndex,
    column,
    columnIndex,
    afterRenderCallBack,
    cellData
  ) => {
    const { value, source } = cellData;
    const {
      forDrillDown,
      subTotalMapping,
      reportPart,
      printingMode,
      getFilterInfoForSubReport,
      isDisplayRowExpander,
      renderExpandIcon
    } = this.props;
    const currentRow = rowData.currentRow;
    const currentRowInfo = rowData.rowDataObjects[columnData.uniqueFieldNameAlias];
    const previewDataFieldsMapping = reportPart.previewDataFieldsMapping;
    const reportPartContent = reportPart.reportPartContent;
    const rowExpanders = reportPartContent.haveRowExpander;
    let displayValue = '';

    const fieldElement = columnData.element;

    !!get('data', value) && source && (source.record = head(get('currentRow.rowDataObject.data', rowData)));

    const formatDisplayData = (originalValue, displayValue) =>
      ReportPartUtils.getDataFormat(
        {
          value: displayValue,
          originalValue
        },
        {
          alternativeText: get('properties.dataFormattings.alternativeText', fieldElement),
          fieldFormatData: get('properties.dataFormattings.format.format', fieldElement),
          fieldDataType: get('previewDataType', fieldElement)
        },
        source
      );

    if (
      currentRow.isSupTotalRow &&
      (!forDrillDown || columnData.type !== ColumnDataObject.COLUMN_TYPE.ROW || currentRow.rowDataObject.isExpand)
    ) {
      const isDisplayCell = currentRow.rowDataObject.uniqueFieldNameAlias === columnData.uniqueFieldNameAlias;
      return (
        <SubTotalCell
          fieldElement={fieldElement}
          columnData={columnData}
          rowData={rowData}
          subTotalMapping={subTotalMapping}
          forDrillDown={forDrillDown}
          rowExpanders={rowExpanders}
          isDisplayCell={isDisplayCell}
        />
      );
    }

    if (!!value.isEmpty) {
      return null;
    }

    if (!value.isAllRow) {
      displayValue = formatDisplayData(value.orgData, value.data);
      const numberOfPointActions = get('element.numberOfPointActions', columnData) || 0;

      if (
        !value.dataNotFound &&
        numberOfPointActions > 0 &&
        (columnData.type === ColumnDataObject.COLUMN_TYPE.ROW || columnData.type === ColumnDataObject.COLUMN_TYPE.VALUE)
      ) {
        const id = `${columnIndex}.${rowIndex}`;

        displayValue = SubReportLinkUtils.applySubReportCellLink(
          reportPart,
          displayValue,
          columnData,
          rowData,
          previewDataFieldsMapping,
          afterRenderCallBack,
          printingMode,
          this.renderListeners,
          id,
          getFilterInfoForSubReport
        );
      }
    } else {
      displayValue = value.data;
    }

    return (
      <span className="u_fullInlineBlock" title={displayValue}>
        {isDisplayRowExpander(currentRowInfo, columnData) && renderExpandIcon(currentRowInfo.rowDataObject)}
        {displayValue}
      </span>
    );
  };

  renderGroupingHeadersExporting = (page: any, numberOfRow: any, columnLocInfoOriginal: any) => {
    const props = this.props;
    const { getCellData, fnGetHeaderCellContentForPivotHeader } = this.props;
    const gridWidth = get('reportPart.reportPartContent.properties.columns.width.value', props);
    const parentHeader = page.columnDataObject;
    const from = page.from;
    const to = page.to;

    if (
      parentHeader.type === ColumnDataObject.COLUMN_TYPE.COLUMN &&
      every({ type: ColumnDataObject.COLUMN_TYPE.VALUE }, parentHeader.childs)
    ) {
      // if this is last level of Column it only have pivot column = 1;
      return this.renderGroupingHeaders([parentHeader], numberOfRow, columnLocInfoOriginal)[0];
    }
    const childPages = PivotExportingUtils.breakChildrenColumn(parentHeader, from, to);

    const childPageVirtualDom = this.renderChildPage(childPages, numberOfRow, columnLocInfoOriginal);
    const columnWidthValue = get('element.properties.dataFormattings.width.value', parentHeader);
    const width = columnWidthValue || gridWidth || undefined;

    return (
      <ColumnGroup
        getTitle={this.getTitle.bind(this, parentHeader)}
        width={width}
        cell={null}
        fnGetCellData={getCellData(parentHeader)}
        fnGetCellStyle={stylingUtil.getCellStyleForPivotHeader.bind(this, parentHeader, columnLocInfoOriginal)}
        fnGetCellContentStyle={fnGetHeaderCellContentForPivotHeader(parentHeader)}
        isHidden={parentHeader.isHidden}
        key={parentHeader.key}
        fnGetHeaderCellStyle={null}
        fnGetHeaderCellContentStyle={null}
      >
        {childPageVirtualDom}
      </ColumnGroup>
    );
  };

  /**
   * Generate ColumnGroup/Column
   * @param  {array} columnDataObjects
   * @return {array} ColumnGroup
   */
  renderGroupingHeaders = (columnDataObjects: any, numberOfRow: any, columnLocInfoOriginal: any) => {
    let headers = [];
    const {
      reportPart,
      getCellData,
      getCellStyle,
      fnGetHeaderCellContentForPivotHeader,
      getCellContentStyle,
      fnGetHeaderCellStyle,
      fnGetHeaderCellContentStyle
    } = this.props;
    const reportPartContent = reportPart.reportPartContent;
    const gridWidth = get('properties.columns.width.value', reportPartContent);
    const columnExpanders = getOr(false, 'columns.properties.useExpanders', reportPartContent);

    if (isEmpty(columnDataObjects)) {
      return null;
    }

    const columnLocInfo = clone(columnLocInfoOriginal) || {
      firstColumn: true,
      lastColumn: true,
      deep: 0
    };

    columnLocInfo.deep++;

    const lastColumnIndex = columnDataObjects.length - 1;
    const firstVisibleIndex = findIndex(negate(get('isHidden')), columnDataObjects);

    columnDataObjects.map((header, index) => {
      const ComponentType = header.isLeaf ? Column : ColumnGroup;

      const currentColumnLocInfo = clone(columnLocInfo);

      currentColumnLocInfo.firstColumn &&
        index !== firstVisibleIndex &&
        index !== 0 &&
        (currentColumnLocInfo.firstColumn = false);
      currentColumnLocInfo.lastColumn && index !== lastColumnIndex && (currentColumnLocInfo.lastColumn = false);

      let sideTotalArray = [];

      if (header.isExpand) {
        const sideTotalColumLocInfo = clone(currentColumnLocInfo);

        sideTotalColumLocInfo.firstColumn = false;
        sideTotalArray = this.renderExpandSideTotal(header, numberOfRow, sideTotalColumLocInfo);
      }

      currentColumnLocInfo.lastColumn && !isEmpty(sideTotalArray) && (currentColumnLocInfo.lastColumn = false);

      const columnWidthValue = get('element.properties.dataFormattings.width.value', header);
      const width = columnWidthValue || gridWidth || undefined;
      header.canRenderHeader = true;
      if (header.element && header.element.properties) {
        header.canRenderHeader = header.element.properties.fieldItemVisible;
      }

      headers.push(
        <ComponentType
          getTitle={this.getTitle.bind(this, header)}
          width={width}
          isInvisible={header.isInvisible}
          canRenderHeader={header.canRenderHeader}
          cell={header.isLeaf ? this.renderCellContent.bind(this, header) : null}
          fnGetCellData={getCellData(header)}
          fnGetCellStyle={
            header.isLeaf
              ? getCellStyle(header, currentColumnLocInfo, numberOfRow)
              : stylingUtil.getCellStyleForPivotHeader.bind(this, header, currentColumnLocInfo)
          }
          fnGetCellContentStyle={
            header.isLeaf ? getCellContentStyle(header) : fnGetHeaderCellContentForPivotHeader(header)
          }
          key={header.key + index}
          fnGetHeaderCellStyle={header.isLeaf ? fnGetHeaderCellStyle(header, currentColumnLocInfo, numberOfRow) : null}
          fnGetHeaderCellContentStyle={header.isLeaf ? fnGetHeaderCellContentStyle(header) : null}
          iconExpandCollapse={this.renderCollapsedIcon(header)}
          isColumnHeader={header.isColumnHeader && header.level === 1}
        >
          {header.isExpand || !columnExpanders
            ? this.renderGroupingHeaders(
                sortBy(getOr(0, 'element.position'), header.childs),
                numberOfRow,
                currentColumnLocInfo
              )
            : this.renderCollapsedSideTotal(header.sideTotals, numberOfRow, header.level, currentColumnLocInfo)}
        </ComponentType>
      );

      headers = concat(headers, sideTotalArray);
    });

    return headers;
  };
}

export default connect(actions)(ReportPartGridPivot);



// WEBPACK FOOTER //
// ./ui/modules/ReportDesigner/Fields/ReportBody/ReportPartType/ReportPartGrid/Izenda.ReportPartGridPivot.js